import { Badge, Group, Stack, Text, useMantineColorScheme } from "@mantine/core";
import { useTranslation } from "../../../hooks/useTranslation";
import { EmployeeAllocationSummary } from "../models/employeeOverview.model";

interface AllocationBadgeGroupProps {
    allocations: EmployeeAllocationSummary[];
}

/** Project, cost center, and work schedule render as separate badges (not one combined badge per
 *  allocation) — each allocation's three badges are kept together on their own row so a split
 *  employee's multiple allocations stay visually grouped. */
export function AllocationBadgeGroup({ allocations }: AllocationBadgeGroupProps) {
    const { colorScheme } = useMantineColorScheme();
    const { t } = useTranslation();
    const costCenterColor = colorScheme === "dark" ? "brand.2" : "brand.9";
    const badgeColor = colorScheme === "dark" ? "brand.1" : "brand.6";

    if (allocations.length === 0) {
        return (
            <Text size="xs" c="dimmed">
                {t("EMPLOYEEOVERVIEW_NO_ALLOCATION", undefined, "No current allocation")}
            </Text>
        );
    }

    return (
        <Stack gap={4} role="list" aria-label={t("EMPLOYEEOVERVIEW_CURRENT_ALLOCATIONS", undefined, "Current allocations")}>
            {allocations.map((allocation, index) => (
                <Stack key={`${allocation.projectName ?? "unknown"}-${index}`} gap={4} role="listitem">
                    {allocation.costCenterName && (
                        <Badge variant="transparent" color={costCenterColor} radius="sm" style={{ textTransform: "none", paddingLeft: 0 }}>
                            {allocation.costCenterName}
                        </Badge>
                    )}
                    <Group gap={4} wrap="wrap">
                        <Badge color={badgeColor} variant="light" radius="sm" style={{ textTransform: "none" }}>
                            {allocation.projectName ?? t("EMPLOYEEOVERVIEW_UNKNOWN_PROJECT", undefined, "Unknown project")}
                            {allocation.allocationPercent != null ? ` · ${allocation.allocationPercent}%` : ""}
                        </Badge>
                        {allocation.workScheduleName && (
                            <Badge color={badgeColor} variant="light" radius="sm" style={{ textTransform: "none" }}>
                                {allocation.workScheduleName}
                            </Badge>
                        )}
                    </Group>
                </Stack>
            ))}
        </Stack>
    );
}
